import React, { useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { CalendarDaysIcon, CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { classNames } from '../utils/classNames';

// A period of whole months, for example the stay months of a report table. The button shows the period
// ("May – Oct 2026"), and the name of the preset when the period is one ("Last 6 months · May – Oct 2026").
// It opens a panel: the presets of the caller on the left, one or two years of months on the right.
// - A click on a preset applies it and closes the panel.
// - On the months, the first click picks one end and the second click the other end (the order does not
//   matter). Between the two clicks, the months under the pointer show the period. The panel closes after
//   the second click. A second click on the same month gives a period of one month.
// - Months before `min` or after `max` are disabled. The arrows change the year.
// - `years` 2: two years side by side, so a period over a year shows while the user picks it. The left year
//   holds the previous arrow, the right year the next arrow; each arrow moves both years by one.
// - `maxMonths`: after the first click, a month that gives a longer period is disabled, and the hint says the limit.
// - `variant` 'bare': no ring and no shadow, 32 px high, for a picker inside a frame that already has an edge
//   (a CaptionGroup). 'button' (the default): the white toolbar button with a ring.
// Below the sm width the presets sit above the months, and two years stack. The Component knows nothing about the data.

export type MonthRange = {
	// YYYY-MM, from <= to.
	from: string,
	to: string,
};

export type MonthRangePreset = {
	id: string,
	label: string,
	range: MonthRange,
};

type Props = {
	value: MonthRange,
	onChange: (range: MonthRange) => void,
	presets?: MonthRangePreset[],
	// The first and the last month that the user can pick, YYYY-MM.
	min?: string,
	max?: string,
	// The longest period, in months, that a pick on the months can give.
	maxMonths?: number,
	// The years of months in the panel.
	years?: 1 | 2,
	variant?: 'button' | 'bare',
	// The accessible name of the button ("Stay months of the table").
	label: string,
	disabled?: boolean,
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const keyOf = (year: number, month: number) => `${year}-${String(month + 1).padStart(2, '0')}`;
const nameOf = (key: string) => `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
// The number of months from a to b, both included ("2026-04" to "2027-09" = 18).
const spanOf = (a: string, b: string) => Math.abs((Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 + Number(b.slice(5, 7)) - Number(a.slice(5, 7))) + 1;

// "Oct 2026" · "May – Oct 2026" · "Nov 2026 – Feb 2027".
export const formatMonthRange = (range: MonthRange): string => {
	if (range.from === range.to) return nameOf(range.from);
	if (range.from.slice(0, 4) === range.to.slice(0, 4)) return `${MONTHS[Number(range.from.slice(5, 7)) - 1]} – ${nameOf(range.to)}`;
	return `${nameOf(range.from)} – ${nameOf(range.to)}`;
};

const sameRange = (a: MonthRange, b: MonthRange) => a.from === b.from && a.to === b.to;

const ARROW = 'rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';

const Months: React.FunctionComponent<{
	value: MonthRange, min?: string, max?: string, maxMonths?: number, years: 1 | 2, onPick: (range: MonthRange) => void,
}> = ({ value, min, max, maxMonths, years, onPick }) => {
	// One year: the year of the end of the period. Two years: the year of its start on the left.
	const [year, setYear] = useState(Number((years === 2 ? value.from : value.to).slice(0, 4)));
	const [first, setFirst] = useState<string | null>(null);
	const [hover, setHover] = useState<string | null>(null);
	const shown: MonthRange = first === null ? value
		: { from: [first, hover ?? first].sort()[0], to: [first, hover ?? first].sort()[1] };
	const minYear = min === undefined ? null : Number(min.slice(0, 4));
	const maxYear = max === undefined ? null : Number(max.slice(0, 4));
	const lastYear = year + years - 1;
	const off = (key: string) => (min !== undefined && key < min) || (max !== undefined && key > max)
		|| (first !== null && maxMonths !== undefined && spanOf(first, key) > maxMonths);

	const pick = (key: string) => {
		if (first === null) {
			setFirst(key);
			return;
		}
		onPick({ from: [first, key].sort()[0], to: [first, key].sort()[1] });
	};

	const grid = (y: number, previous: boolean, next: boolean) => <div key={y} className="px-3 pt-3 sm:w-72">
		<div className="mb-2 flex items-center justify-between">
			{previous
				? <button type="button" aria-label="Previous year" disabled={minYear !== null && year <= minYear} onClick={() => setYear(year - 1)} className={ARROW}>
					<ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
				</button>
				: <span aria-hidden="true" className="h-7 w-7" />}
			<p className="text-sm font-semibold text-gray-900" aria-live="polite">{y}</p>
			{next
				? <button type="button" aria-label="Next year" disabled={maxYear !== null && lastYear >= maxYear} onClick={() => setYear(year + 1)} className={ARROW}>
					<ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
				</button>
				: <span aria-hidden="true" className="h-7 w-7" />}
		</div>
		<div className="grid grid-cols-3 gap-1" onMouseLeave={() => setHover(null)}>
			{MONTHS.map((name, month) => {
				const key = keyOf(y, month);
				const end = key === shown.from || key === shown.to;
				const inside = key > shown.from && key < shown.to;
				const disabled = off(key);
				return <button key={key} type="button" disabled={disabled} aria-pressed={end || inside}
					aria-label={`${name} ${y}`} onClick={() => pick(key)} onMouseEnter={() => setHover(key)} onFocus={() => setHover(key)}
					className={classNames(
						'h-9 rounded-md text-sm tabular-nums focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600',
						disabled ? 'cursor-not-allowed text-gray-300'
							: (end ? 'bg-blue-600 font-semibold text-white'
								: (inside ? 'bg-blue-50 font-medium text-blue-700 hover:bg-blue-100' : 'text-gray-700 hover:bg-gray-100')),
					)}>
					{name}
				</button>;
			})}
		</div>
	</div>;

	const limit = maxMonths === undefined ? '' : ` (up to ${maxMonths} months)`;
	return <div>
		{years === 2
			? <div className="flex flex-col sm:flex-row sm:divide-x sm:divide-gray-100">{grid(year, true, false)}{grid(year + 1, false, true)}</div>
			: grid(year, true, true)}
		<p className="px-3 pb-3 pt-2 text-xs text-gray-500" aria-live="polite">
			{first === null ? 'Pick the first month, then the last.' : `From ${nameOf(first)}: now pick the last month${limit}.`}
		</p>
	</div>;
};

const MonthRangePicker: React.FunctionComponent<Props> = ({
	value, onChange, presets = [], min, max, maxMonths, years = 1, variant = 'button', label, disabled = false,
}) => {
	const active = presets.find(p => sameRange(p.range, value));
	return <Popover className="relative">
		<PopoverButton aria-label={`${label}: ${active === undefined ? '' : `${active.label}, `}${formatMonthRange(value)}`} disabled={disabled}
			className={classNames(
				'inline-flex max-w-full items-center gap-x-2 rounded-md bg-white text-sm font-medium text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:cursor-not-allowed disabled:opacity-50',
				variant === 'bare' ? 'h-8 px-2 hover:bg-gray-50' : 'h-9 px-3 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
			)}>
			<CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
			<span className="truncate">
				{active !== undefined && <span className="font-normal text-gray-500">{active.label} · </span>}
				{formatMonthRange(value)}
			</span>
			<ChevronDownIcon aria-hidden="true" className="-mr-1 h-4 w-4 shrink-0 text-gray-400" />
		</PopoverButton>
		<PopoverPanel anchor={{ to: 'bottom end', gap: 8 }}
			className="z-50 w-80 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-none sm:w-auto">
			{({ close }) => <div className="flex flex-col sm:flex-row">
				{presets.length > 0 && <ul className="border-b border-gray-100 p-2 sm:w-44 sm:border-b-0 sm:border-r">
					{presets.map(preset => {
						const on = active?.id === preset.id;
						return <li key={preset.id}>
							<button type="button" aria-pressed={on} onClick={() => { onChange(preset.range); close(); }}
								className={classNames('flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600',
									on ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700 hover:bg-gray-50')}>
								{preset.label}
								{on && <CheckIcon aria-hidden="true" className="h-4 w-4 shrink-0" />}
							</button>
						</li>;
					})}
				</ul>}
				<Months value={value} min={min} max={max} maxMonths={maxMonths} years={years} onPick={range => { onChange(range); close(); }} />
			</div>}
		</PopoverPanel>
	</Popover>;
};

export default MonthRangePicker;
